'use client';

import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { SwatchBookIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';

import { useTranslations } from '@northladder/i18n';
import {
  TThemeColorScheme,
  TThemeMode,
  useBuyerAppConfig,
} from '@northladder/services';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  IDropdownOption,
  SidebarMenuButton,
} from '../../ui';

import { ActiveThemeLabel } from './ActiveThemeLabel';
import { colorPaletteOptions, themeModeOptions } from './themeConfig';
import { useThemeStore } from './useThemeStore';

interface IThemeControlWidgetProps {
  className?: string;
  type?: 'button' | 'sidebar-menu-item';
  size?: 'small' | 'large';
}

export function ThemeControlWidget({
  className,
  size = 'large',
  type = 'sidebar-menu-item',
}: IThemeControlWidgetProps) {
  const t = useTranslations('DesignSystemComponents.ThemeWidget');
  const { theme, setTheme } = useTheme();
  const { colorScheme: currentColorScheme, setColorScheme } = useThemeStore();
  const { canSwitchColorScheme } = useBuyerAppConfig();
  const Trigger = type === 'button' ? Button : SidebarMenuButton;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const handleSetColorPalette = (item: IDropdownOption<TThemeColorScheme>) => {
    const colorScheme = item.id;
    const themeClasses = colorPaletteOptions.map((color) => color.id);

    if (themeClasses?.includes(colorScheme)) {
      document.documentElement.classList.remove(...themeClasses);
      document.documentElement.classList.add(colorScheme);
      setColorScheme(colorScheme);
    }
  };

  const handleSetThemeMode = (item: IDropdownOption<TThemeMode>) => {
    setTheme(item.id);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Trigger
          className="
            group transition-all
            duration-300
            group-data-[state=collapsed]:justify-center
            group-data-[state=expanded]:space-x-2.5
          "
          data-compact={size}
          size="lg"
          {...(type === 'button' ? { variant: 'outline' } : {})}
        >
          <SwatchBookIcon className="size-4 shrink-0 stroke-[1.25]" />

          <span
            className="
              block text-start group-data-[compact=small]:hidden
              group-data-[state=collapsed]:hidden
            "
          >
            <span className="line-clamp-none block text-sm/5 font-normal">
              {t('label')}
            </span>
            <span className="text-text-caption block text-xs/5">
              {mounted ? <ActiveThemeLabel /> : null}
            </span>
          </span>
          <ChevronDownIcon
            className="
              size-4 ms-auto shrink-0 group-data-[state=collapsed]:hidden
            "
          />
        </Trigger>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-text-caption">
          {t('instruction')}
        </DropdownMenuLabel>

        {themeModeOptions.map((item) => {
          const { description, id, label, IconBefore, IconActive } = item;

          const isActive = id === theme;
          const Icon = isActive ? IconActive : IconBefore;

          const handleOnClickItem = () => {
            handleSetThemeMode(item);
          };

          return (
            <DropdownMenuItem
              key={id}
              data-selected={isActive}
              onClick={handleOnClickItem}
              title={description ? t(description) : undefined}
            >
              {Icon ? <Icon className="text-inherit" /> : null}
              {t(label)}
              {isActive ? <CheckIcon className="ms-auto text-inherit" /> : null}
            </DropdownMenuItem>
          );
        })}

        {canSwitchColorScheme ? (
          <>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-text-caption">
              NOTE: This section is for internal testing purposes only and will
              be removed on live deployments. This is to allow for quick testing
              between different color brand options to catch breaking changes.
            </DropdownMenuLabel>

            {colorPaletteOptions.map((item) => {
              const { id, label, description } = item;
              const isActive = id === currentColorScheme;

              const handleOnClickItem = () => {
                handleSetColorPalette(item);
              };

              return (
                <DropdownMenuItem
                  key={id}
                  data-selected={isActive}
                  onClick={handleOnClickItem}
                >
                  <span className="flex w-full flex-col">
                    {label}
                    <span className="text-text-caption w-full text-xs">
                      {description}
                    </span>
                  </span>

                  {isActive ? (
                    <CheckIcon className="ms-auto text-inherit" />
                  ) : null}
                </DropdownMenuItem>
              );
            })}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
